/*
  <tat-mini-buy>: the buy panel of "Shop the product in this review" (snippets/article-product-showcase). The option
  picker updates the price and the button (Add to cart, or Enquire about stock for a sold-out option, which opens the
  contact pop-up with the product filled in). Add to cart uses TAT.cart and opens the cart drawer.

  <tat-store-popup>: the floating showroom card on review articles (snippets/article-store-popup).
  Appears once the reader is about a third of the way down the page, or after 20 seconds of reading, unless it was
  closed in the last 7 days. Steps aside while the in-article promo or the product showcase ([data-popup-avoid]) is on
  screen, so it never covers their buttons. Booking and contact pop-ups open above it (they are modal dialogs).
*/
const KEY = 'tat-store-popup-closed';
const QUIET_DAYS = 7;

class TatStorePopup extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    try {
      const closed = Number(localStorage.getItem(KEY));
      if (closed && Date.now() - closed < QUIET_DAYS * 864e5) return;
    } catch {
      // Storage blocked: just show it.
    }
    this.querySelector('[data-popup-close]')?.addEventListener('click', () => this.dismiss());
    this.onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > 0.33) this.show();
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.timer = setTimeout(() => this.show(), 20000);

    const avoid = document.querySelectorAll('[data-popup-avoid]');
    if (avoid.length && 'IntersectionObserver' in window) {
      const onScreen = new Set();
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? onScreen.add(e.target) : onScreen.delete(e.target)));
        this.toggleAttribute('data-aside', onScreen.size > 0);
      });
      avoid.forEach((el) => this.observer.observe(el));
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    clearTimeout(this.timer);
    this.observer?.disconnect();
  }

  show() {
    if (this.shown) return;
    this.shown = true;
    window.removeEventListener('scroll', this.onScroll);
    clearTimeout(this.timer);
    this.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => this.toggleAttribute('data-visible', true)));
  }

  dismiss() {
    this.removeAttribute('data-visible');
    this.observer?.disconnect();
    setTimeout(() => (this.hidden = true), 500);
    try {
      localStorage.setItem(KEY, String(Date.now()));
    } catch {
      // Storage blocked: it will show again next visit.
    }
  }
}

if (!customElements.get('tat-store-popup')) customElements.define('tat-store-popup', TatStorePopup);

class TatMiniBuy extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.picker = this.querySelector('[data-mini-variant]');
    this.button = this.querySelector('[data-mini-add]');
    this.picker?.addEventListener('change', () => this.sync());
    this.button?.addEventListener('click', (e) => {
      if (this.button.hasAttribute('data-modal-open')) return; // sold out: the contact pop-up opens instead
      e.preventDefault();
      this.add();
    });
  }

  current() {
    const el = this.picker;
    if (!el) return null;
    const option = el.tagName === 'SELECT' ? el.selectedOptions[0] : el;
    return { id: Number(el.value), price: option?.dataset.price, available: option?.dataset.available === 'true', title: option?.textContent?.trim() };
  }

  sync() {
    const v = this.current();
    if (!v || !this.button) return;
    const price = this.querySelector('[data-mini-price]');
    if (price && v.price) price.textContent = v.price;
    this.querySelector('[data-mini-error]').textContent = '';
    if (v.available) {
      this.button.removeAttribute('data-modal-open');
      this.button.textContent = 'Add to cart';
    } else {
      const name = `${this.dataset.productTitle} (${v.title.replace(/\s*\(enquire\)$/, '')})`;
      this.button.dataset.modalOpen = 'contact';
      this.button.dataset.topic = 'Price and availability';
      this.button.dataset.message = `Hi, I'm interested in the ${name}. Could you let me know about stock and timing?`;
      this.button.textContent = 'Enquire about stock';
    }
  }

  async add() {
    const v = this.current();
    if (!v || !window.TAT?.cart) return;
    const label = this.button.textContent;
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    this.button.textContent = 'Adding...';
    try {
      await window.TAT.cart.add({ items: [{ id: v.id, quantity: 1 }] });
      const drawer = document.getElementById('CartDrawer');
      if (window.TAT.settings?.cartType === 'drawer' && typeof drawer?.open === 'function') drawer.open(this.button);
      else window.location.href = window.TAT.routes?.cart || '/cart';
    } catch (err) {
      this.querySelector('[data-mini-error]').textContent =
        err?.json?.description || (err?.status ? err.message : '') || 'Sorry, we could not add this to your cart. Please try again.';
    } finally {
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
      this.button.textContent = label;
    }
  }
}

if (!customElements.get('tat-mini-buy')) customElements.define('tat-mini-buy', TatMiniBuy);
